/**
 * What the business spends, and the recurring templates that stamp out the standing monthly costs.
 *
 * The port of `Views/ExpensesPage.xaml` + `ExpensesViewModel`. Nothing else in the CRM records
 * spending - an invoice is money in and a purchase is the cost of a part that was resold - so without
 * this page a profit figure is gross profit under the wrong name. The two lists share a page because
 * "why is rent missing from March?" is answered by the template, not the list.
 *
 * What carries over:
 *
 *   * DATES ARE CALENDAR DAYS, STORED AT MIDDAY UTC. Local midnight would land in the previous UTC day
 *     west of Greenwich and slide an expense into the wrong quarter at a boundary.
 *   * UNTICKED "PAID" MEANS OUTSTANDING: it counts on the accrual side and stays out of cash until it
 *     clears. Ticked with no date falls back to the incurred date, which is what a card payment is.
 *   * DIRECT COST is a choice the category cannot make - the same hardware is stock for one job and
 *     an office laptop for another - so it is its own tick box with the sentence saying what it does.
 *   * SAVING A TEMPLATE ADDS THE MONTHS IT ALREADY OWES, server-side, so the list is re-read with it.
 *
 * Gone with OpenSilver: the "written out as text" date labels beside every picker. They existed
 * because that DatePicker rendered its watermark instead of its value; Webwin's shows the value.
 */
import type { Container } from 'webwin';
import * as crm from '../api/crm.js';
import { describeError } from '../api/http.js';
import { ExpenseCategory, type ExpenseDto, type ExpenseListDto, type RecurringExpenseDto } from '../contracts.gen.js';
import { join, money, plural } from '../format.js';
import { isoDayOf, middayUtcFromIso, todayIso } from '../recurrence.js';
import { CardList } from '../ui/CardList.js';
import type { Card } from '../ui/Card.js';
import { chip } from '../ui/Chip.js';
import { dateBox } from '../ui/DateBox.js';
import { confirm } from '../ui/dialogs.js';
import { EmptyState } from '../ui/EmptyState.js';
import { moneyBox } from '../ui/MoneyBox.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  checkBox,
  comboBox,
  field,
  fieldRow,
  fillWith,
  FONT,
  GAP,
  h1,
  h2,
  muted,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  text,
  textBox,
  value,
  vstack
} from '../ui/layout.js';
import { loadCustomerChoices, type CustomerChoices } from './sections/recurring.js';
import type { CrmPage, PageContext } from './page.js';

/** In enum order, so an index IS the enum value - the same arrangement the C# view model used. */
const CATEGORY_NAMES = [
  'Payroll', 'Subcontractor', 'Software', 'Hardware', 'Rent', 'Utilities', 'Insurance', 'Vehicle',
  'Marketing', 'Professional fees', 'Training', 'Telecom', 'Bank and fees', 'Tax', 'Other'
] as const;

/** `yyyy-01-01` of this year in the reader's calendar: an expense page opened in April shows April. */
function startOfYearIso(): string {
  return `${new Date().getFullYear()}-01-01`;
}

function firstOfMonthIso(): string {
  return todayIso().slice(0, 8) + '01';
}

/** A calendar day as the instant the server stores it at. */
function midday(iso: string): string | null {
  return middayUtcFromIso(iso)?.toISOString() ?? null;
}

/** Local midnight of a day, as an instant - plus `days`, for an exclusive upper bound. */
function localMidnight(iso: string, days = 0): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days).toISOString() : undefined;
}

export function expensesPage(): CrmPage {
  return {
    mount(host: Container, ctx: PageContext) {
      let choices: CustomerChoices = { names: [], ids: [] };
      let editingId = 0;
      let editingRecurringId = 0;
      let busy = false;

      const status = new StatusLine();

      // =================================================================
      //  Header
      // =================================================================

      const titleRow = splitRow({ align: 'center' });
      fillWith(titleRow, h1('Expenses'));
      titleRow.controls.add(button('New expense', () => beginNew(), true));
      const header = stackOf(
        vstack({ spacing: GAP.xs }),
        titleRow,
        muted(
          'What the business spends. This is the other half of the profit and loss statement - without it, profit is only gross profit.'
        )
      );

      // The customer pickers: index 0 is ordinary overhead, belonging to no customer.
      const customerItems = (): string[] => ['No customer (overhead)', ...choices.names];
      const customerIdAt = (index: number): number | null => (index > 0 ? (choices.ids[index - 1] ?? null) : null);
      const customerIndexOf = (id: number | null): number => (id === null ? 0 : Math.max(0, choices.ids.indexOf(id) + 1));

      // =================================================================
      //  Expense editor
      // =================================================================

      const editorTitle = h2('New expense');
      const incurred = dateBox({ iso: todayIso() });
      const category = comboBox<string>([...CATEGORY_NAMES], { selectedIndex: ExpenseCategory.Software });
      const amount = moneyBox();
      const descriptionBox = textBox();
      const vendorBox = textBox();
      const customer = comboBox<string>(['No customer (overhead)'], { selectedIndex: 0 });
      const directCheck = checkBox('Direct cost of delivering work', false);
      const paidCheck = checkBox('Paid', true);
      const paid = dateBox({ iso: todayIso() });
      paidCheck.on('change', () => {
        paid.picker.enabled = paidCheck.checked;
      });
      const notesBox = textBox();
      const editorStatus = new StatusLine();

      const editor = card({ visible: false });
      editor.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          editorTitle,
          fieldRow(field('What for', descriptionBox), field('Vendor', vendorBox)),
          fieldRow(field('Incurred', incurred.picker), field('Category', category), field('Amount', amount.row)),
          fieldRow(field('Customer', customer), stackOf(vstack({ spacing: GAP.xs }), muted(' '), directCheck)),
          muted(
            'A direct cost sits in cost of sales and reduces gross profit - a subcontractor on a job, hardware bought for one engagement. Leave it unticked for overhead, which reduces net profit only.'
          ),
          fieldRow(stackOf(vstack({ spacing: GAP.xs }), muted(' '), paidCheck), field('Paid on', paid.picker)),
          muted('Unpaid counts on the accrual side and stays out of cash until it clears.'),
          field('Notes', notesBox),
          stackOf(buttonRow(), button('Save', () => void save(), true), button('Cancel', () => closeEditor())),
          editorStatus.label
        )
      );

      // =================================================================
      //  Filters, totals and the list
      // =================================================================

      const from = dateBox({ iso: startOfYearIso() });
      const to = dateBox({ iso: todayIso() });
      const categoryFilter = comboBox<string>(['Any category', ...CATEGORY_NAMES], {
        selectedIndex: 0,
        onSelectionChanged: () => void refresh()
      });
      const searchBox = textBox({ placeholder: 'Description or vendor' });
      searchBox.on('keydown', (e: { key: string }) => {
        if (e.key === 'Enter') void refresh();
      });
      const unpaidCheck = checkBox('Unpaid only', false);
      unpaidCheck.on('change', () => void refresh());
      const totalLine = text('');

      const filters = card();
      filters.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          stackOf(
            buttonRow({ spacing: GAP.md, align: 'end' }),
            field('From', from.picker, { width: 150 }),
            field('To', to.picker, { width: 150 }),
            field('Category', categoryFilter, { width: 170 }),
            field('Search', searchBox, { width: 200 }),
            unpaidCheck
          ),
          stackOf(buttonRow(), button('Apply', () => void refresh(), true), button('Reset', () => void reset())),
          totalLine
        )
      );

      const list = new CardList<ExpenseDto>({ key: e => e.id, build: e => buildExpenseCard(e) });
      const empty = new EmptyState(
        'No expenses in this range. Record what the business spends - wages, subscriptions, rent, insurance - and the profit and loss statement stops reading as pure profit.'
      );

      // =================================================================
      //  Recurring
      // =================================================================

      const recurringStatus = new StatusLine();
      const recurringTitle = h2('New recurring expense');
      const rDescription = textBox();
      const rCategory = comboBox<string>([...CATEGORY_NAMES], { selectedIndex: ExpenseCategory.Rent });
      const rAmount = moneyBox();
      const rVendor = textBox();
      const rDay = textBox({ text: '1' });
      const rStart = dateBox({ iso: firstOfMonthIso() });
      const rEnd = dateBox();
      const rCustomer = comboBox<string>(['No customer (overhead)'], { selectedIndex: 0 });
      const rActive = checkBox('Active', true);
      const rDirect = checkBox('Direct cost', false);
      const rAutoPaid = checkBox('Paid automatically', true);
      const rNotes = textBox();
      const recurringEditorStatus = new StatusLine();

      const recurringEditor = card({ visible: false });
      recurringEditor.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          recurringTitle,
          fieldRow(field('What for', rDescription), field('Vendor', rVendor)),
          fieldRow(field('Category', rCategory), field('Amount per month', rAmount.row), field('Day of the month', rDay)),
          fieldRow(field('From', rStart.picker), field('Until (optional)', rEnd.picker)),
          field('Customer', rCustomer),
          stackOf(buttonRow({ spacing: GAP.lg }), rActive, rDirect, rAutoPaid),
          muted(
            'Paid automatically suits a direct debit or card subscription - each generated entry is stamped paid on the day it falls. Untick it where the bill is settled by hand later, and the entries arrive unpaid.'
          ),
          field('Notes', rNotes),
          stackOf(
            buttonRow(),
            button('Save', () => void saveRecurring(), true),
            button('Cancel', () => closeRecurringEditor())
          ),
          recurringEditorStatus.label
        )
      );

      const recurringList = new CardList<RecurringExpenseDto>({ key: t => t.id, build: t => buildRecurringCard(t) });
      const recurringEmpty = new EmptyState('No recurring expenses yet. Add rent, wages or a subscription once, and each month is filled in for you.');

      const recurringSection = stackOf(
        vstack({ spacing: GAP.md }),
        h2('Recurring'),
        muted(
          "Standing monthly costs. Each one stamps out a real expense every month, so a statement for last quarter doesn't depend on anyone remembering to type them in."
        ),
        stackOf(
          buttonRow(),
          button('New recurring', () => beginNewRecurring(), true),
          button('Generate due entries', () => void runRecurring())
        ),
        recurringStatus.label,
        recurringEditor,
        recurringEmpty.panel,
        recurringList.panel
      );

      const { view, content } = scrollPage();
      content.controls.add(
        stackOf(
          vstack({ spacing: GAP.lg }),
          header,
          status.label,
          editor,
          filters,
          empty.panel,
          list.panel
        )
      );
      content.controls.add(recurringSection);
      host.controls.add(view);

      // =================================================================
      //  Rows
      // =================================================================

      function buildExpenseCard(e: ExpenseDto): Card {
        const heading = buttonRow({ spacing: GAP.sm });
        heading.controls.add(value(e.description || '(no description)', { font: FONT.bold }));
        heading.controls.add(chip(e.categoryName, 'neutral'));
        if (e.directCost) heading.controls.add(chip('direct cost', 'info'));
        if (e.recurringId !== null) heading.controls.add(chip('recurring', 'info'));

        const body = stackOf(vstack({ spacing: GAP.xs }), heading);
        const who = join([e.vendor, e.customerName]);
        if (who) body.controls.add(muted(who));
        body.controls.add(
          text(
            join([
              money(e.amount),
              `incurred ${isoDayOf(e.incurredUtc)}`,
              e.paidUtc ? `paid ${isoDayOf(e.paidUtc)}` : null
            ]),
            { font: FONT.small }
          )
        );
        if (!e.paidUtc) body.controls.add(muted('Unpaid - counts on the accrual side only.', 'warn'));

        const actions = buttonRow();
        if (!e.paidUtc) actions.controls.add(button('Mark paid', () => void markPaid(e), true));
        actions.controls.add(button('Edit', () => beginEdit(e)));
        actions.controls.add(button('Delete', () => void remove(e)));
        body.controls.add(actions);

        const shell = card();
        shell.controls.add(body);
        return shell;
      }

      function buildRecurringCard(t: RecurringExpenseDto): Card {
        const heading = buttonRow({ spacing: GAP.sm });
        heading.controls.add(value(t.description || '(no description)', { font: FONT.bold }));
        heading.controls.add(chip(t.categoryName, 'neutral'));
        if (!t.active) heading.controls.add(chip('paused', 'warn'));
        if (t.directCost) heading.controls.add(chip('direct cost', 'info'));

        const body = stackOf(vstack({ spacing: GAP.xs }), heading);
        const who = join([t.vendor, t.customerName]);
        if (who) body.controls.add(muted(who));
        body.controls.add(
          text(
            join([
              `${money(t.amount)} a month, on day ${t.dayOfMonth}`,
              t.endUtc ? `until ${isoDayOf(t.endUtc)}` : null,
              `${plural(t.generatedCount, 'entry', 'entries')} so far`,
              t.lastGeneratedUtc ? `last ${isoDayOf(t.lastGeneratedUtc)}` : null,
              t.autoMarkPaid ? 'paid automatically' : 'arrives unpaid'
            ]),
            { font: FONT.small }
          )
        );
        body.controls.add(
          stackOf(buttonRow(), button('Edit', () => beginEditRecurring(t)), button('Remove', () => void removeRecurring(t)))
        );

        const shell = card();
        shell.controls.add(body);
        return shell;
      }

      // =================================================================
      //  Expense editing
      // =================================================================

      function beginNew(): void {
        editingId = 0;
        editorTitle.text = 'New expense';
        incurred.set(todayIso());
        paid.set(todayIso());
        paidCheck.checked = true;
        paid.picker.enabled = true;
        category.selectedIndex = ExpenseCategory.Software;
        descriptionBox.text = '';
        vendorBox.text = '';
        amount.set(null);
        customer.selectedIndex = 0;
        directCheck.checked = false;
        notesBox.text = '';
        openEditor();
      }

      function beginEdit(e: ExpenseDto): void {
        editingId = e.id;
        editorTitle.text = `Edit ${e.description}`;
        incurred.set(isoDayOf(e.incurredUtc));
        paidCheck.checked = e.paidUtc !== null;
        paid.set(isoDayOf(e.paidUtc ?? e.incurredUtc));
        paid.picker.enabled = paidCheck.checked;
        category.selectedIndex = e.category;
        // LiteDB reads a stored "" back as null, whatever the contract says.
        descriptionBox.text = e.description ?? '';
        vendorBox.text = e.vendor ?? '';
        amount.set(e.amount);
        customer.selectedIndex = customerIndexOf(e.customerId);
        directCheck.checked = e.directCost;
        notesBox.text = e.notes ?? '';
        openEditor();
      }

      function openEditor(): void {
        editorStatus.clear();
        editor.visible = true;
        relayout(content);
        descriptionBox.focus();
      }

      function closeEditor(): void {
        editor.visible = false;
        relayout(content);
      }

      async function save(): Promise<void> {
        if (busy) return;
        const description = descriptionBox.text.trim();
        if (!description) {
          editorStatus.error('An expense needs a description.');
          return;
        }
        const value = amount.value();
        if (value === null || value <= 0) {
          editorStatus.error('Enter an amount greater than zero.');
          return;
        }
        const incurredUtc = midday(incurred.iso() || todayIso());
        if (!incurredUtc) {
          editorStatus.error('Pick the day the expense was incurred.');
          return;
        }
        const body = {
          incurredUtc,
          paidUtc: paidCheck.checked ? midday(paid.iso() || incurred.iso() || todayIso()) : null,
          category: (category.selectedIndex as ExpenseCategory) ?? ExpenseCategory.Other,
          description,
          vendor: vendorBox.text.trim(),
          amount: value,
          customerId: customerIdAt(customer.selectedIndex),
          directCost: directCheck.checked,
          notes: notesBox.text.trim()
        };
        busy = true;
        editorStatus.busy('Saving…');
        try {
          if (editingId === 0) await crm.createExpense(body);
          else await crm.updateExpense(editingId, body);
          if (ctx.cancelled()) return;
          status.ok(editingId === 0 ? `Recorded ${description}.` : `Saved ${description}.`);
          closeEditor();
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) editorStatus.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function markPaid(e: ExpenseDto): Promise<void> {
        if (busy) return;
        busy = true;
        status.busy(`Marking ${e.description} paid…`);
        try {
          await crm.updateExpense(e.id, {
            incurredUtc: e.incurredUtc,
            paidUtc: midday(todayIso()),
            category: e.category,
            description: e.description ?? '',
            vendor: e.vendor ?? '',
            amount: e.amount,
            customerId: e.customerId,
            directCost: e.directCost,
            notes: e.notes ?? ''
          });
          if (ctx.cancelled()) return;
          status.ok(`Marked ${e.description} paid.`);
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function remove(e: ExpenseDto): Promise<void> {
        if (busy) return;
        const ok = await confirm(ctx.desktop, {
          title: 'Delete expense',
          message: `Delete "${e.description}" (${money(e.amount)})? Every profit-and-loss period that included it will change.`
        });
        if (!ok || ctx.cancelled()) return;
        busy = true;
        status.busy(`Deleting ${e.description}…`);
        try {
          await crm.deleteExpense(e.id);
          if (ctx.cancelled()) return;
          status.ok(`Deleted ${e.description}.`);
          if (editingId === e.id) closeEditor();
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      // =================================================================
      //  Recurring editing
      // =================================================================

      function beginNewRecurring(): void {
        editingRecurringId = 0;
        recurringTitle.text = 'New recurring expense';
        rDescription.text = '';
        rCategory.selectedIndex = ExpenseCategory.Rent;
        rAmount.set(null);
        rVendor.text = '';
        rDay.text = '1';
        rStart.set(firstOfMonthIso());
        rEnd.set(null);
        rCustomer.selectedIndex = 0;
        rActive.checked = true;
        rDirect.checked = false;
        rAutoPaid.checked = true;
        rNotes.text = '';
        openRecurringEditor();
      }

      function beginEditRecurring(t: RecurringExpenseDto): void {
        editingRecurringId = t.id;
        recurringTitle.text = `Edit ${t.description}`;
        rDescription.text = t.description ?? '';
        rCategory.selectedIndex = t.category;
        rAmount.set(t.amount);
        rVendor.text = t.vendor ?? '';
        rDay.text = String(t.dayOfMonth);
        rStart.set(isoDayOf(t.startUtc));
        rEnd.set(t.endUtc ? isoDayOf(t.endUtc) : null);
        rCustomer.selectedIndex = customerIndexOf(t.customerId);
        rActive.checked = t.active;
        rDirect.checked = t.directCost;
        rAutoPaid.checked = t.autoMarkPaid;
        rNotes.text = t.notes ?? '';
        openRecurringEditor();
      }

      function openRecurringEditor(): void {
        recurringEditorStatus.clear();
        recurringEditor.visible = true;
        relayout(content);
        rDescription.focus();
      }

      function closeRecurringEditor(): void {
        recurringEditor.visible = false;
        relayout(content);
      }

      async function saveRecurring(): Promise<void> {
        if (busy) return;
        const description = rDescription.text.trim();
        if (!description) {
          recurringEditorStatus.error('A recurring expense needs a description.');
          return;
        }
        const value = rAmount.value();
        if (value === null || value <= 0) {
          recurringEditorStatus.error('Enter a monthly amount greater than zero.');
          return;
        }
        const day = Number.parseInt(rDay.text.trim(), 10);
        if (!Number.isInteger(day) || day < 1 || day > 31) {
          recurringEditorStatus.error('Day of the month must be between 1 and 31.');
          return;
        }
        const startUtc = midday(rStart.iso() || todayIso());
        if (!startUtc) {
          recurringEditorStatus.error('Pick the month it starts.');
          return;
        }
        const body = {
          category: (rCategory.selectedIndex as ExpenseCategory) ?? ExpenseCategory.Other,
          description,
          vendor: rVendor.text.trim(),
          amount: value,
          dayOfMonth: day,
          startUtc,
          endUtc: rEnd.iso() ? midday(rEnd.iso()) : null,
          active: rActive.checked,
          directCost: rDirect.checked,
          customerId: customerIdAt(rCustomer.selectedIndex),
          autoMarkPaid: rAutoPaid.checked,
          notes: rNotes.text.trim()
        };
        busy = true;
        recurringEditorStatus.busy('Saving…');
        try {
          if (editingRecurringId === 0) await crm.createRecurringExpense(body);
          else await crm.updateRecurringExpense(editingRecurringId, body);
          if (ctx.cancelled()) return;
          recurringStatus.ok(`Saved ${description}. Any months it already owed have been added.`);
          closeRecurringEditor();
          await Promise.all([refreshRecurring(), refresh()]);
        } catch (error) {
          if (!ctx.cancelled()) recurringEditorStatus.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function removeRecurring(t: RecurringExpenseDto): Promise<void> {
        if (busy) return;
        const ok = await confirm(ctx.desktop, {
          title: 'Remove recurring expense',
          message: `Stop "${t.description}" recurring? The ${plural(t.generatedCount, 'entry', 'entries')} it already created are kept.`
        });
        if (!ok || ctx.cancelled()) return;
        busy = true;
        recurringStatus.busy(`Removing ${t.description}…`);
        try {
          await crm.deleteRecurringExpense(t.id);
          if (ctx.cancelled()) return;
          recurringStatus.ok(`Removed the recurring entry for ${t.description}. Past entries are unchanged.`);
          if (editingRecurringId === t.id) closeRecurringEditor();
          await refreshRecurring();
        } catch (error) {
          if (!ctx.cancelled()) recurringStatus.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function runRecurring(): Promise<void> {
        if (busy) return;
        busy = true;
        recurringStatus.busy('Generating due entries…');
        try {
          const result = await crm.runRecurringExpenses();
          if (ctx.cancelled()) return;
          recurringStatus.ok(
            result.created === 0
              ? result.templatesConsidered === 1
                ? 'Nothing due - the recurring entry is up to date.'
                : `Nothing due - all ${result.templatesConsidered} recurring entries are up to date.`
              : `Added ${plural(result.created, 'entry', 'entries')} from ${plural(result.templatesConsidered, 'recurring template')}.`
          );
          await Promise.all([refreshRecurring(), refresh()]);
        } catch (error) {
          if (!ctx.cancelled()) recurringStatus.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      // =================================================================
      //  Loading
      // =================================================================

      function render(result: ExpenseListDto): void {
        list.set(result.expenses);
        empty.update(result.expenses.length);
        totalLine.text = join([
          `${plural(result.count, 'entry', 'entries')}`,
          `${money(result.total)} total`,
          `${money(result.paidTotal)} paid`,
          `${money(result.unpaidTotal)} outstanding`,
          `${from.iso() || 'the beginning'} to ${to.iso() || 'now'}`
        ]);
        relayout(content);
      }

      async function refresh(): Promise<void> {
        const categoryIndex = categoryFilter.selectedIndex;
        try {
          const result = await crm.getExpenses(
            {
              fromUtc: localMidnight(from.iso()),
              // Exclusive end of the "to" day, so the whole day is included.
              toUtc: localMidnight(to.iso(), 1),
              category: categoryIndex > 0 ? ((categoryIndex - 1) as ExpenseCategory) : undefined,
              search: searchBox.text,
              unpaidOnly: unpaidCheck.checked
            },
            ctx.dispose.signal()
          );
          if (ctx.cancelled()) return;
          render(result);
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not load the expenses: ' + describeError(error));
        }
      }

      async function refreshRecurring(): Promise<void> {
        try {
          const templates = await crm.getRecurringExpenses(ctx.dispose.signal());
          if (ctx.cancelled()) return;
          recurringList.set(templates);
          recurringEmpty.update(templates.length);
          relayout(content);
        } catch (error) {
          if (!ctx.cancelled()) recurringStatus.error('Could not load the recurring expenses: ' + describeError(error));
        }
      }

      async function reset(): Promise<void> {
        from.set(startOfYearIso());
        to.set(todayIso());
        categoryFilter.selectedIndex = 0;
        searchBox.text = '';
        unpaidCheck.checked = false;
        await refresh();
      }

      async function initialize(): Promise<void> {
        choices = await loadCustomerChoices(ctx.dispose.signal());
        if (ctx.cancelled()) return;
        customer.items = customerItems();
        rCustomer.items = customerItems();
        customer.selectedIndex = 0;
        rCustomer.selectedIndex = 0;
        await Promise.all([refresh(), refreshRecurring()]);
      }

      void initialize();
    }
  };
}
